import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    makeStyles,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PDFViewerV2, { PDFViewerV2Props } from './PDFViewerV2';

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiDialog-paperScrollPaper': {
            backgroundColor: 'transparent',
        },
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: '#fff',
        padding: '8px 16px',
    },
    titleText: {
        textAlign: 'center',
        flex: 1,
    },
    closeButton: {
        color: '#fff',
        padding: theme.spacing(1),
    },
    dialogContent: {
        padding: '0 !important',
        overflow: 'auto',
    },
    contentContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100%',
        padding: theme.spacing(2),
    },
}));

export interface PdfViewerModalV2Props extends Omit<PDFViewerV2Props, 'file'> {
    /** Whether the modal is open */
    open: boolean;
    /** Callback when modal is closed */
    onClose?: () => void;
    /** PDF file URL, File object, or ArrayBuffer */
    file: string | File | ArrayBuffer | null | undefined;
    /** Modal title (optional) */
    title?: string;
    /** Whether to show modal title */
    showTitle?: boolean;
    /** Additional props to pass to Modal component */
    modalProps?: Record<string, any>;
}

const PdfViewerModalV2: React.FC<PdfViewerModalV2Props> = ({
    open,
    onClose,
    file,
    title = 'PDF Viewer',
    showTitle = false,
    modalProps = {},
    ...pdfViewerProps
}) => {
    const classes = useStyles();
    const [isOpen, setIsOpen] = useState<boolean>(open);

    useEffect(() => {
        setIsOpen(open);
    }, [open]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        onClose?.();
    }, [onClose]);

    return (
        <Dialog
            onClose={handleClose}
            open={isOpen}
            fullScreen={true}
            className={classes.dialog}
            {...modalProps}
        >
            <DialogTitle disableTypography className={classes.dialogTitle}>
                {showTitle && <Box className={classes.titleText}>{title}</Box>}
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={handleClose}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Box className={classes.contentContainer}>
                    <PDFViewerV2 file={file} {...pdfViewerProps} />
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default PdfViewerModalV2;
